import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { ObservationCard } from '@/components/observation/ObservationCard';
import { SearchBar } from '@/components/ui/SearchBar';
import { Colors } from '@/constants/Colors';
import { useTheme } from '@/hooks/useTheme';
import { apiService } from '@/services/api';
import { matchesObservationAuthor, matchesObservationOwner } from '@/services/observationTeam';
import { useObservationStore } from '@/stores/observationStore';
import { useProfileStore } from '@/stores/profileStore';
import {
  buildMatchLookupMap,
  enrichObservationWithMatchMap,
  enrichObservationsWithMatchMap,
} from '@/utils/enrichObservationWithMatch';
import { mergeObservations } from '@/utils/mergeObservations';
import { isObservationVisibleToViewer } from '@/utils/observationDisplay';
import { useMatchStore } from '@/stores/matchStore';
import type { Observation } from '@/types';

const TABS = [
  { id: 'recent', label: 'Mes observations' },
  { id: 'top', label: 'Top' },
] as const;

type TabId = (typeof TABS)[number]['id'];

function groupByMonth(observations: Observation[]): Array<{ key: string; label: string; items: Observation[] }> {
  const groups = new Map<string, { label: string; items: Observation[] }>();
  for (const obs of observations) {
    let key = 'sans-date';
    let label = 'SANS DATE';
    if (obs.date) {
      const d = new Date(obs.date);
      if (!isNaN(d.getTime())) {
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).toUpperCase();
      }
    }
    const existing = groups.get(key);
    if (existing) existing.items.push(obs);
    else groups.set(key, { label, items: [obs] });
  }
  return Array.from(groups.entries())
    .sort((a, b) => {
      if (a[0] === 'sans-date') return 1;
      if (b[0] === 'sans-date') return -1;
      return a[0] < b[0] ? 1 : -1;
    })
    .map(([key, value]) => ({ key, ...value }));
}

function belongsToUser(
  obs: Observation,
  tifosiKey: string,
  profile: { pseudo?: string; email?: string; prenom?: string; nom?: string },
): boolean {
  if (matchesObservationOwner(obs, tifosiKey)) return true;
  return matchesObservationAuthor(obs, profile);
}

export default function ScoutPage() {
  const navigate = useNavigate();
  const colors = useTheme();
  const [activeTab, setActiveTab] = useState<TabId>('recent');
  const [searchQuery, setSearchQuery] = useState('');
  const [mine, setMine] = useState<Observation[]>([]);
  const [isMineLoading, setIsMineLoading] = useState(false);
  const [mineLoadError, setMineLoadError] = useState<string | null>(null);
  const tifosiCle = useProfileStore((state) => state.tifosiCle);
  const profileEmail = useProfileStore((state) => state.email);
  const profilePseudo = useProfileStore((state) => state.pseudo);
  const profilePrenom = useProfileStore((state) => state.prenom);
  const profileNom = useProfileStore((state) => state.nom);
  const googleId = useProfileStore((state) => state.googleId);
  const ensureTifosiCle = useProfileStore((state) => state.ensureTifosiCle);
  const { all, isLoading, fetchAll, getTop } = useObservationStore();
  const matches = useMatchStore((state) => state.matches);
  const fetchMatches = useMatchStore((state) => state.fetchMatches);
  const matchById = useMemo(() => buildMatchLookupMap(matches), [matches]);

  const profileForMatch = useMemo(
    () => ({
      pseudo: profilePseudo,
      email: profileEmail,
      prenom: profilePrenom,
      nom: profileNom,
    }),
    [profilePseudo, profileEmail, profilePrenom, profileNom],
  );

  useEffect(() => {
    fetchAll();
    void fetchMatches();
  }, [fetchAll, fetchMatches]);

  useEffect(() => {
    let isMounted = true;
    const loadMine = async (forceRefresh = false) => {
      if (!googleId) {
        if (isMounted) {
          setMine([]);
          setMineLoadError(null);
        }
        return;
      }
      setIsMineLoading(true);
      setMineLoadError(null);
      try {
        const cle = (await ensureTifosiCle()) || (tifosiCle || '').trim();
        if (!cle) {
          if (isMounted) {
            setMine([]);
            setMineLoadError('Profil Tifosi introuvable. Reconnectez-vous via Google depuis Profil.');
          }
          return;
        }
        const [byTifosi, allObs] = await Promise.all([
          apiService.getObservationsByTifosi(cle, forceRefresh),
          forceRefresh
            ? apiService.getObservations(true)
            : Promise.resolve({ data: useObservationStore.getState().all, fromCache: false }),
        ]);
        let merged = byTifosi.data;
        if (merged.length === 0 && allObs.data.length > 0) {
          merged = allObs.data.filter((obs) => belongsToUser(obs, cle, profileForMatch));
        }
        const fromStore = useObservationStore.getState().all.filter((obs) =>
          belongsToUser(obs, cle, profileForMatch),
        );
        const combined = mergeObservations(merged, fromStore);
        if (isMounted) setMine(enrichObservationsWithMatchMap(combined, matchById));
      } catch (error) {
        if (isMounted) {
          setMine([]);
          setMineLoadError(error instanceof Error ? error.message : 'Impossible de charger vos observations.');
        }
      } finally {
        if (isMounted) setIsMineLoading(false);
      }
    };
    loadMine();
    return () => {
      isMounted = false;
    };
  }, [tifosiCle, googleId, ensureTifosiCle, profileForMatch, matchById]);

  const refreshActiveTab = async () => {
    if (activeTab === 'top') {
      await fetchAll(true);
      return;
    }
    setIsMineLoading(true);
    setMineLoadError(null);
    try {
      const cle = (await ensureTifosiCle()) || (tifosiCle || '').trim();
      if (!cle) {
        setMine([]);
        setMineLoadError('Profil Tifosi introuvable. Reconnectez-vous via Google depuis Profil.');
        return;
      }
      const [byTifosi, allObs] = await Promise.all([
        apiService.getObservationsByTifosi(cle, true),
        apiService.getObservations(true),
      ]);
      let merged = byTifosi.data;
      if (merged.length === 0) {
        merged = allObs.data.filter((obs) => belongsToUser(obs, cle, profileForMatch));
      }
      setMine(enrichObservationsWithMatchMap(mergeObservations(merged), matchById));
    } catch (error) {
      setMine([]);
      setMineLoadError(error instanceof Error ? error.message : 'Impossible de charger vos observations.');
    } finally {
      setIsMineLoading(false);
    }
  };

  const observations = useMemo(() => {
    if (activeTab === 'top') {
      return getTop(50).filter((obs) => isObservationVisibleToViewer(obs, tifosiCle));
    }
    return [...mine].sort((a, b) => {
      const ta = a.date ? new Date(a.date).getTime() : 0;
      const tb = b.date ? new Date(b.date).getTime() : 0;
      return tb - ta;
    });
  }, [activeTab, all, getTop, mine, tifosiCle]);

  const filteredObservations = useMemo(() => {
    if (activeTab !== 'recent') return observations;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return observations;
    return observations.filter((obs) => {
      const fields = [
        obs.prenom,
        obs.nom,
        obs.playerName,
        obs.playerNumber,
        obs.club,
        obs.teamName,
        obs.poste,
        obs.comments,
        obs.matchInfo,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      return fields.some((value) => value.includes(q));
    });
  }, [activeTab, observations, searchQuery]);

  const groups = useMemo(() => (activeTab === 'recent' ? groupByMonth(filteredObservations) : null), [activeTab, filteredObservations]);
  const shouldRenderGrouped = activeTab === 'recent' && (groups?.length ?? 0) > 0;
  const openObservation = (obs: Observation) => {
    navigate('/observation/detailed', {
      state: {
        mode: 'edit',
        trustedMineList: activeTab === 'recent',
        observation: obs,
      },
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0, width: '100%', backgroundColor: colors.background }}>
      <div
        style={{
          flexShrink: 0,
          boxSizing: 'border-box',
          width: '100%',
          padding: '0 16px',
          borderBottom: `1px solid ${Colors.primary}15`,
          backgroundColor: colors.background,
        }}
      >
        <h1 style={styles.visuallyHidden}>Scouting</h1>
        <div style={styles.tabBarRow}>
          <div style={{ display: 'flex', flex: '1 1 0%', minWidth: 0, alignItems: 'stretch' }}>
            {TABS.map((tab) => (
              <button
                type="button"
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: '1 1 0%',
                  minWidth: 0,
                  paddingTop: 12,
                  paddingBottom: 12,
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderTop: 'none',
                  borderBottom: `2px solid ${activeTab === tab.id ? Colors.primary : 'transparent'}`,
                  font: 'inherit',
                  color: 'inherit',
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 700, color: activeTab === tab.id ? Colors.primary : colors.textSecondary }}>
                  {tab.label}
                </span>
              </button>
            ))}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              paddingRight: 0
            }}
          >
            <button
              type="button"
              onClick={refreshActiveTab}
              aria-label="Actualiser"
              style={{ ...styles.iconButton, flexShrink: 0, backgroundColor: colors.surface, border: 'none', cursor: 'pointer' }}
            >
              <Icon name="refresh" size={22} color={colors.text} />
            </button>
          </div>
        </div>
        {activeTab === 'recent' && (
          <div style={{ paddingBottom: 12, paddingTop: 12 }}>
            <SearchBar value={searchQuery} onChangeText={setSearchQuery} placeholder="Recherche libre..." />
          </div>
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, paddingBottom: 100 }}>
        {(activeTab === 'top' ? isLoading : isMineLoading) && filteredObservations.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <div className="spinner" />
          </div>
        ) : filteredObservations.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', borderRadius: 16, border: `1px dashed ${colors.border}`, backgroundColor: colors.surface }}>
            <Icon name="analytics-outline" size={40} color={colors.textSecondary} />
            <div style={{ fontSize: 14, color: colors.textSecondary, marginTop: 12 }}>
              {mineLoadError
                ? mineLoadError
                : activeTab === 'recent' && searchQuery.trim()
                  ? 'Aucun resultat pour cette recherche'
                  : 'Aucune observation pour le moment'}
            </div>
            <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4 }}>
              {mineLoadError
                ? 'Allez dans Profil et reconnectez Google si le problème persiste.'
                : 'Ouvrez un match et appuyez sur "Ajouter une observation" pour commencer.'}
            </div>
          </div>
        ) : shouldRenderGrouped ? (
          (groups ?? []).map((group) => (
            <div key={group.key}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, marginBottom: 12, marginTop: 8, padding: '0 8px', color: `${Colors.primary}99` }}>
                {group.label}
              </div>
              {group.items.map((obs) => {
                const enriched = enrichObservationWithMatchMap(obs, matchById);
                return (
                  <ObservationCard
                    key={obs.id}
                    observation={enriched}
                    onPress={() => openObservation(enriched)}
                  />
                );
              })}
            </div>
          ))
        ) : (
          filteredObservations.map((obs) => {
            const enriched = enrichObservationWithMatchMap(obs, matchById);
            return (
              <ObservationCard
                key={obs.id}
                observation={enriched}
                onPress={() => openObservation(enriched)}
              />
            );
          })
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  tabBarRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    width: '100%',
    minWidth: 0,
    gap: 8,
  },
  iconButton: { width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
};
